import React, { useEffect } from 'react';
import NumberFormat from 'react-number-format';
// internal imports
import { useSetState } from '~/app/Utility/customHooks';
import { validatePhoneNumber } from '~/app/Utility/general';
import WebHelpers from '~/app/webHelpers.js';
// external imports
import {
  AppBar,
  Toolbar,
  FormControl,
  InputLabel,
  Input,
  Divider,
  Button,
  FormControlLabel,
  Checkbox,
  FormHelperText,
  Tooltip,
  Grid,
} from '@material-ui/core';
import InfoIcon from '@material-ui/icons/Info';
import CheckBoxIcon from '@material-ui/icons/CheckBox';
import CheckBoxOutlineBlankIcon from '@material-ui/icons/CheckBoxOutlineBlank';
// redux imports
import { useSelector, useDispatch } from 'react-redux';
import { actions as setupPageActions } from '~/app/redux/setupPages/duck';
import { formHasErrors } from '../databaseSetupHelper';

const UpsertEmployer = props => {
  const dispatch = useDispatch();

  const { isEditingRecord, currentEditRecord } = useSelector(state => ({
    isEditingRecord: state.setupPageState.isEditingRecord,
    currentEditRecord: state.setupPageState.currentEditRecord,
  }));

  const [state, setState] = useSetState({
    expandedUSAddress: isEditingRecord,
    expandedForeignAddress: isEditingRecord,
    foreignAddress: currentEditRecord.hidden_domestic_ind,
    inputErrors: {},
    isSubmitDisabled: false,
    season: WebHelpers.getJWTPayload().season,
  });

  useEffect(() => {
    setState({
      isSubmitDisabled: formHasErrors(currentEditRecord, handleSetErrors),
    });
  }, [currentEditRecord]);

  const handleSetErrors = errors => {
    setState({ inputErrors: errors }); // set any errors to the local state
  };

  useEffect(() => {
    if (
      currentEditRecord.hidden_domestic_ind === undefined &&
      currentEditRecord.hidden_foreign_ind === undefined
    ) {
      dispatch(setupPageActions.setHiddenForeignInd());
    }
  }, []);

  const handleInputChange = e => {
    const name = e.target.name;
    e.persist();
    dispatch(setupPageActions.updateCurrentEditRecord(e));

    // Clear out inputErrors if user is modifying field value
    const tempErrors = state.inputErrors;
    if (tempErrors[name]) {
      delete tempErrors[name];
      setState({
        inputErrors: tempErrors,
      });
    }
  };

  const handleUpsert = () => {
    props.handleUserDBUpsert(currentEditRecord);
  };

  const handleDomesticCheckbox = e => {
    setState({ foreignAddress: e.target.checked });
    if (e.target.checked) {
      dispatch(setupPageActions.clearAddressData(false, true));
    } else {
      dispatch(setupPageActions.clearAddressData(true, false));
    }
  };

  const validateFields = () => {
    const phoneDigits = currentEditRecord.payers_phone?.replace(/[^0-9]/g, '');
    const isPayersPhoneInvalid = phoneDigits && phoneDigits.length !== 10;

    if (
      currentEditRecord.address1 == null ||
      currentEditRecord.address1 === '' ||
      currentEditRecord.postal_code == null ||
      currentEditRecord.postal_code === '' ||
      currentEditRecord.employer_ein == null ||
      currentEditRecord.employer_ein === '' ||
      currentEditRecord.firmname == null ||
      currentEditRecord.firmname === '' ||
      isPayersPhoneInvalid
    ) {
      return false;
    }

    // Province may be undef since its not loaded initially
    if (
      (state.foreignAddress &&
        (typeof currentEditRecord.province === 'undefined' || currentEditRecord.province === '')) ||
      (!state.foreignAddress && currentEditRecord.state_code === '')
    ) {
      return false;
    }

    return true;
  };

  return (
    <div>
      <AppBar position="static">
        <Toolbar style={styles.toolbar}>
          {isEditingRecord ? 'Edit ' : 'Add New '}
          Employer/Payer
        </Toolbar>
      </AppBar>
      <form styles={styles.form}>
        <Grid
          container
          spacing={8}
          style={{
            marginBottom: '2em',
            marginTop: '2em',
            textAlign: 'left',
          }}
        >
          <Grid item xs={8} style={styles.sectionHeading}>
            Employer EIN Information
          </Grid>
          <Grid item xs={6} style={{ float: 'left', marginLeft: '1em' }}>
            <FormControl>
              <InputLabel style={styles.labelStyles} required={false} shrink>
                Federal EIN
              </InputLabel>
              <Input
                id="txtEINIDUpsertEmployerEIN"
                name="employer_ein"
                value={currentEditRecord.employer_ein}
                onChange={handleInputChange}
                style={styles.inputStyle}
                error={
                  !currentEditRecord.employer_ein ||
                  currentEditRecord.employer_ein === '' ||
                  state.inputErrors?.employer_ein !== undefined
                }
                inputProps={{
                  maxLength: '9',
                }}
                disableUnderline
              />
              {state.inputErrors?.employer_ein && (
                <FormHelperText>{state.inputErrors.employer_ein}</FormHelperText>
              )}
            </FormControl>
          </Grid>
          <Grid item style={{ float: 'none' }}>
            <FormControl>
              <InputLabel style={styles.labelStyles} required={false} shrink>
                Name Control
              </InputLabel>
              <Input
                id="txtNameControlUpsertEmployerNamecrtl"
                name="firmnamectrl"
                value={currentEditRecord.firmnamectrl}
                onChange={handleInputChange}
                style={styles.inputStyle}
                error={state.inputErrors?.firmnamectrl !== undefined}
                inputProps={{
                  maxLength: '4',
                }}
                disableUnderline
              />
              {state.inputErrors?.firmnamectrl && (
                <FormHelperText>{state.inputErrors.firmnamectrl}</FormHelperText>
              )}
            </FormControl>
          </Grid>
          <br />
          <Grid item xs={6} style={{ float: 'left', marginLeft: '1em' }}>
            <FormControl>
              <InputLabel style={styles.labelStyles} required={false} shrink>
                Company name
              </InputLabel>
              <Input
                id="txtNameControlUpsertEmployerName"
                name="firmname"
                value={currentEditRecord.firmname}
                onChange={handleInputChange}
                style={styles.inputStyle}
                error={
                  !currentEditRecord.firmname ||
                  currentEditRecord.firmname === '' ||
                  state.inputErrors?.firmname !== undefined
                }
                inputProps={{
                  maxLength: '75',
                }}
                disableUnderline
              />
              {state.inputErrors?.firmname && (
                <FormHelperText>{state.inputErrors.firmname}</FormHelperText>
              )}
            </FormControl>
          </Grid>
          <Grid item style={{ float: 'none' }}>
            <FormControl>
              <InputLabel style={styles.labelStyles} shrink>
                {`Payer's Phone Number `}
                {state.season < 2025 && (
                  <Tooltip title="Available for Season 2025 and later.">
                    <InfoIcon fontSize="small" />
                  </Tooltip>
                )}
              </InputLabel>
              <NumberFormat
                id="txtPayersPhone"
                name="payers_phone"
                value={state.season < 2025 ? '' : currentEditRecord.payers_phone}
                onChange={handleInputChange}
                style={styles.inputStyle}
                format="(###) ###-####"
                customInput={Input}
                disableUnderline
                error={
                  !!currentEditRecord.payers_phone &&
                  !validatePhoneNumber(currentEditRecord.payers_phone)
                }
                disabled={state.season < 2025} // field is only used seasons 2025 and up
              />
            </FormControl>
          </Grid>
        </Grid>

        <Divider style={{ marginTop: '1em', marginBottom: '1em' }} />
        <Grid
          container
          spacing={8}
          style={{
            marginTop: '2em',
            flexWrap: 'nowrap',
          }}
        >
          <Grid item xs={8} style={styles.sectionHeading}>
            {state.foreignAddress ? 'Foreign Address' : 'Domestic Address'}
          </Grid>
          <Grid item xs={4}>
            <FormControlLabel
              label="Use Foreign Address"
              control={
                <Checkbox
                  style={{ width: 17, height: 16, textAlign: 'right' }}
                  icon={<CheckBoxOutlineBlankIcon style={{ fontSize: 24 }} color="secondary" />}
                  checkedIcon={<CheckBoxIcon style={{ fontSize: 24 }} color="primary" />}
                  id="chkDomesticAddrEduInst"
                  name="domesticCheckboxEduInst"
                  checked={state.foreignAddress}
                  onChange={handleDomesticCheckbox}
                  inputProps={{ height: '20px', width: '20px' }}
                />
              }
            />
          </Grid>
        </Grid>

        {(currentEditRecord.hidden_foreign_ind === true || !isEditingRecord) && (
          <Grid
            container
            spacing={8}
            style={{
              textAlign: 'left',
            }}
          >
            {!state.foreignAddress && (
              <Grid container>
                <Grid item xs={6} style={{ float: 'left', marginLeft: '1em' }}>
                  <FormControl>
                    <InputLabel style={styles.labelStyles} required={false} shrink>
                      Care of Address
                    </InputLabel>
                    <Input
                      id="txtEINIDUpsertEmployerCOAddress"
                      name="address2"
                      value={currentEditRecord.address2}
                      onChange={handleInputChange}
                      style={styles.inputStyle}
                      inputProps={{
                        maxLength: '75',
                      }}
                      error={state.inputErrors?.address2 !== undefined}
                      disableUnderline
                    />
                    {state.inputErrors?.address2 && (
                      <FormHelperText>{state.inputErrors.address2}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>
                <Grid item style={{ float: 'none' }}>
                  <FormControl>
                    <InputLabel style={styles.labelStyles} required={false} shrink>
                      Company Address
                    </InputLabel>
                    <Input
                      id="txtEINIDUpsertEmployerFirmAddress"
                      name="address1"
                      value={currentEditRecord.address1}
                      onChange={handleInputChange}
                      style={styles.inputStyle}
                      error={
                        !currentEditRecord.address1 ||
                        currentEditRecord.address1 === '' ||
                        state.inputErrors?.address1 !== undefined
                      }
                      inputProps={{
                        maxLength: '75',
                      }}
                      disableUnderline
                    />
                    {state.inputErrors?.address1 && (
                      <FormHelperText>{state.inputErrors.address1}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>
                <br />
                <Grid item xs={6} style={{ float: 'left', marginLeft: '1em' }}>
                  <FormControl>
                    <InputLabel style={styles.labelStyles} required={false} shrink>
                      City
                    </InputLabel>
                    <Input
                      id="txtNameControlUpsertEmployerCity"
                      name="city"
                      value={currentEditRecord.city}
                      onChange={handleInputChange}
                      style={styles.inputStyle}
                      error={
                        !currentEditRecord.city ||
                        currentEditRecord.city === '' ||
                        state.inputErrors?.city !== undefined
                      }
                      inputProps={{
                        maxLength: '75',
                      }}
                      disableUnderline
                    />
                    {state.inputErrors?.city && (
                      <FormHelperText>{state.inputErrors.city}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>
                <Grid item style={{ float: 'none' }}>
                  <FormControl>
                    <InputLabel style={styles.labelStyles} shrink>
                      State
                    </InputLabel>
                    <Input
                      id="txtNameControlUpsertEmployerState"
                      name="state_code"
                      value={currentEditRecord.state_code || ''}
                      onChange={handleInputChange}
                      style={styles.inputStyle}
                      error={
                        !currentEditRecord.state_code || state.inputErrors?.state_code !== undefined
                      }
                      inputProps={{
                        maxLength: '2',
                      }}
                      disableUnderline
                    />
                    <FormHelperText>{state.inputErrors?.state_code}</FormHelperText>
                  </FormControl>
                </Grid>
                <Grid
                  item
                  xs={6}
                  style={{
                    float: 'none',
                    marginLeft: '1em',
                  }}
                >
                  <FormControl>
                    <InputLabel style={styles.labelStyles} required={false} shrink>
                      Zip Code
                    </InputLabel>
                    <Input
                      id="txtNameControlUpsertEmployerZipCode"
                      name="postal_code"
                      value={currentEditRecord.postal_code}
                      onChange={handleInputChange}
                      style={styles.inputStyle}
                      error={
                        !currentEditRecord.postal_code ||
                        currentEditRecord.postal_code === '' ||
                        state.inputErrors?.postal_code !== undefined
                      }
                      inputProps={{
                        maxLength: '12',
                      }}
                      disableUnderline
                    />
                    {state.inputErrors?.postal_code && (
                      <FormHelperText>{state.inputErrors.postal_code}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>
              </Grid>
            )}
          </Grid>
        )}

        {(currentEditRecord.hidden_domestic_ind === true || !isEditingRecord) && (
          <Grid
            container
            spacing={8}
            style={{
              marginBottom: '2em',
              textAlign: 'left',
            }}
          >
            {state.foreignAddress && (
              <Grid container>
                <Grid item xs={6} style={{ float: 'left', marginLeft: '1em' }}>
                  <FormControl>
                    <InputLabel style={styles.labelStyles} required={false} shrink>
                      Address 1
                    </InputLabel>
                    <Input
                      id="txtEINIDUpsertEmployerAddress1"
                      name="address1"
                      value={currentEditRecord.address1}
                      onChange={handleInputChange}
                      style={styles.inputStyle}
                      error={
                        !currentEditRecord.address1 ||
                        currentEditRecord.address1 === '' ||
                        state.inputErrors?.address1 !== undefined
                      }
                      inputProps={{
                        maxLength: '75',
                      }}
                      disableUnderline
                    />
                    {state.inputErrors?.address1 && (
                      <FormHelperText>{state.inputErrors.address1}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>
                <Grid item style={{ float: 'none' }}>
                  <FormControl>
                    <InputLabel style={styles.labelStyles} required={false} shrink>
                      Address 2
                    </InputLabel>
                    <Input
                      id="txtNameControlUpsertEmployerAddress2"
                      name="address2"
                      value={currentEditRecord.address2}
                      onChange={handleInputChange}
                      style={styles.inputStyle}
                      inputProps={{
                        maxLength: '75',
                      }}
                      error={state.inputErrors?.address2 !== undefined}
                      disableUnderline
                    />
                    {state.inputErrors?.address2 && (
                      <FormHelperText>{state.inputErrors.address2}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>
                <br />
                <Grid item xs={6} style={{ float: 'left', marginLeft: '1em' }}>
                  <FormControl>
                    <InputLabel style={styles.labelStyles} required={false} shrink>
                      City
                    </InputLabel>
                    <Input
                      id="txtNameControlUpsertEmployerCity"
                      name="city"
                      value={currentEditRecord.city}
                      onChange={handleInputChange}
                      style={styles.inputStyle}
                      error={
                        !currentEditRecord.city ||
                        currentEditRecord.city === '' ||
                        state.inputErrors?.city !== undefined
                      }
                      inputProps={{
                        maxLength: '75',
                      }}
                      disableUnderline
                    />
                    {state.inputErrors?.city && (
                      <FormHelperText>{state.inputErrors.city}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>
                <Grid item style={{ float: 'none' }}>
                  <FormControl>
                    <InputLabel style={styles.labelStyles} required={false} shrink>
                      Province
                    </InputLabel>
                    <Input
                      id="txtNameControlUpsertEmployerProvince"
                      name="province"
                      value={currentEditRecord.province}
                      onChange={handleInputChange}
                      style={styles.inputStyle}
                      error={
                        !currentEditRecord.province ||
                        currentEditRecord.province === '' ||
                        state.inputErrors?.province !== undefined
                      }
                      disableUnderline
                    />
                    {state.inputErrors?.province && (
                      <FormHelperText>{state.inputErrors.province}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>
                <br />
                <Grid item xs={6} style={{ float: 'left', marginLeft: '1em' }}>
                  <FormControl>
                    <InputLabel style={styles.labelStyles} required={false} shrink>
                      Postal Code
                    </InputLabel>
                    <Input
                      id="txtNameControlUpsertEmployerPostalCode"
                      name="postal_code"
                      value={currentEditRecord.postal_code}
                      onChange={handleInputChange}
                      style={styles.inputStyle}
                      error={
                        !currentEditRecord.postal_code ||
                        currentEditRecord.postal_code === '' ||
                        state.inputErrors?.postal_code !== undefined
                      }
                      inputProps={{
                        maxLength: '50',
                      }}
                      disableUnderline
                    />
                    {state.inputErrors?.postal_code && (
                      <FormHelperText>{state.inputErrors.postal_code}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>
                <Grid item style={{ float: 'none' }}>
                  <FormControl>
                    <InputLabel style={styles.labelStyles} required={false} shrink>
                      Country Code
                    </InputLabel>
                    <Input
                      id="txtNameControlUpsertEmployerCountryCode"
                      name="country_code"
                      value={currentEditRecord.country_code}
                      onChange={handleInputChange}
                      style={styles.inputStyle}
                      error={state.inputErrors?.country_code !== undefined}
                      inputProps={{
                        maxLength: '2',
                      }}
                      disableUnderline
                    />
                    {state.inputErrors?.country_code && (
                      <FormHelperText>{state.inputErrors.country_code}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>
              </Grid>
            )}
          </Grid>
        )}

        <Grid
          container
          spacing={8}
          style={{ marginBottom: '2em', marginTop: '2em', textAlign: 'left' }}
        >
          <Divider style={{ marginTop: '1em' }} />
          <Grid item xs={8} style={styles.sectionHeading}>
            State Identification Number
          </Grid>
          <Grid item xs={6} style={{ float: 'left', marginLeft: '1em' }}>
            <FormControl>
              <InputLabel style={styles.labelStyles} required={false} shrink>
                State 1
              </InputLabel>
              <Input
                id="txtEINIDUpsertEmployerState1"
                name="state_code_1"
                value={currentEditRecord.state_code_1}
                onChange={handleInputChange}
                style={styles.inputStyle}
                inputProps={{
                  maxLength: '2',
                }}
                error={state.inputErrors?.state_code_1 !== undefined}
                disableUnderline
              />
              <FormHelperText>{state.inputErrors?.state_code_1}</FormHelperText>
            </FormControl>
          </Grid>
          <Grid item style={{ float: 'none' }}>
            <FormControl>
              <InputLabel style={styles.labelStyles} required={false} shrink>
                State 1 Identification
              </InputLabel>
              <Input
                id="txtEINIDUpsertEmployerStateID1"
                name="state_id_1"
                value={currentEditRecord.state_id_1}
                onChange={handleInputChange}
                style={styles.inputStyle}
                error={state.inputErrors?.state_id_1 !== undefined}
                inputProps={{
                  maxLength: '16',
                }}
                disableUnderline
              />
              {state.inputErrors?.state_id_1 && (
                <FormHelperText>{state.inputErrors.state_id_1}</FormHelperText>
              )}
            </FormControl>
          </Grid>
          <br />
          <Grid item xs={6} style={{ float: 'left', marginLeft: '1em' }}>
            <FormControl>
              <InputLabel style={styles.labelStyles} required={false} shrink>
                State 2
              </InputLabel>
              <Input
                id="txtEINIDUpsertEmployerState2"
                name="state_code_2"
                value={currentEditRecord.state_code_2}
                onChange={handleInputChange}
                style={styles.inputStyle}
                inputProps={{
                  maxLength: '2',
                }}
                error={state.inputErrors?.state_code_2 !== undefined}
                disableUnderline
              />
              <FormHelperText>{state.inputErrors?.state_code_2} </FormHelperText>
            </FormControl>
          </Grid>
          <Grid item style={{ float: 'none' }}>
            <FormControl>
              <InputLabel style={styles.labelStyles} required={false} shrink>
                State 2 Identification
              </InputLabel>
              <Input
                id="txtEINIDUpsertEmployerStateID2"
                name="state_id_2"
                value={currentEditRecord.state_id_2}
                onChange={handleInputChange}
                error={state.inputErrors?.state_id_2 !== undefined}
                style={styles.inputStyle}
                inputProps={{
                  maxLength: '16',
                }}
                disableUnderline
              />
              {state.inputErrors?.state_id_2 && (
                <FormHelperText>{state.inputErrors.state_id_2}</FormHelperText>
              )}
            </FormControl>
          </Grid>
        </Grid>
        <Grid container spacing={8} style={styles.bottomButtons}>
          <Grid item xs={9}>
            <Button
              id="btnCancelAddUUpsertEmployer"
              color="primary"
              onClick={props.handleCloseUpsertModal}
              style={styles.button}
            >
              Cancel
            </Button>
          </Grid>
          <Grid item>
            <Button
              id="btnUpsertEmployer"
              color="primary"
              style={styles.button}
              onClick={handleUpsert}
              disabled={!validateFields() || state.isSubmitDisabled}
            >
              {isEditingRecord ? 'Save' : 'Add'}
            </Button>
          </Grid>
        </Grid>
      </form>
    </div>
  );
};

const styles = {
  toolbar: {
    backgroundColor: '#444A59',
  },
  sectionHeading: {
    color: '#1A173B',
    fontFamily: 'Roboto',
    fontSize: '21px',
    marginLeft: '1em',
    flexWrap: 'nowrap',
  },
  form: {
    margin: '2em',
  },
  button: {
    width: '9em',
    fontSize: '14px',
    borderRadius: '4px',
    marginLeft: '2em',
  },
  labelStyles: {
    color: '#354052',
    textAlign: 'left',
    fontFamily: 'Roboto',
  },
  inputStyle: {
    width: '20vw',
    height: '3.5vh',
  },
  bottomButtons: {
    textAlign: 'right',
    padding: '0',
    marginRight: '2em',
    marginBottom: '2em',
    marginTop: '2em',
    flexWrap: 'nowrap',
  },
};

export default UpsertEmployer;
